import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Caption from 'LuxKit/Typography/Caption'
import CopyToClipboardClickableIcon from 'checkout/Components/Confirmation/BookingDetailsV2/CopyToClipboardClickableIcon'
import { INTL_DMY_CASUAL_SHORT_FORMAT, INTL_LONG_WEEKDAY, INTL_SHORT_TIME_FORMAT_AM_PM } from 'constants/dateFormats'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import IconButton from 'LuxKit/Button/IconButton'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Modal from 'LuxKit/Modal/Modal'
import StarRatingStar from 'components/Common/StarRatingStar'
import Switch from 'LuxKit/Switch/Switch'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { FormattedMessage, useIntl } from 'react-intl'
import ExperiencesQRCode from 'components/Pages/ExperienceVoucher/ExperiencesQRCode'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ChangeEvent, MouseEventHandler } from 'react'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useThingsToKnow from 'hooks/Experiences/useThingsToKnow'
import ModalContext from 'contexts/ModalContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import MarkdownRender from 'LuxKit/MarkdownRender'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import SolidExclamationTriangleIcon from 'LuxKit/Icons/solid/SolidExclamationTriangleIcon'
import type { ExperiencesPassportInclusion } from './experiencesPassportTypes'
import { markExperienceOrderItemAsRedeemed } from 'actions/OrderActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import {
  EXPERIENCES_PASSPORT_HERO_IMAGE_PARAMS,
  getInclusionImages,
} from './ExperiencesPassportItemDetailsModal'
import ExperiencesPassportRouteMap from './ExperiencesPassportRouteMap'

export type ExperiencesPassportVoucherModalResult = 'backToMyEscapes' | undefined

const VoucherModal = styled(Modal)`
  [data-testid="experiences-passport-voucher-modal-header-close-button"] {
    display: none;
  }
`

const Hero = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;

  &.is-muted {
    filter: grayscale(1);
  }
`

const CarouselControl = styled(IconButton)`
  position: absolute;
  top: ${rem(12)};
  z-index: 2;
  color: ${props => props.theme.palette.neutral.default.one};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: ${props => props.theme.shadow.bottom.small};

  &:hover,
  &:focus,
  &:active {
    color: ${props => props.theme.palette.neutral.default.one};
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    border-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }

  &.back {
    inset-inline-start: ${rem(12)};
  }

  &.close {
    inset-inline-end: ${rem(12)};
  }
`

const Content = styled.div`
  padding: ${rem(16)} ${rem(20)} ${rem(20)};

  &.is-final-state {
    padding-bottom: ${rem(10)};
  }
`

const VoucherCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${rem(104)};
  gap: ${rem(16)};
  align-items: center;
  padding: ${rem(16)};
  border-radius: ${rem(8)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const QrCodeCard = styled.div`
  display: grid;
  place-items: center;
  width: ${rem(104)};
  height: ${rem(104)};
  border-radius: ${rem(8)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: ${props => props.theme.shadow.bottom.small};
`

const VoucherCodeRow = styled.div`
  display: flex;
  gap: ${rem(12)};
  align-items: center;

  svg {
    width: ${rem(18)};
    height: ${rem(18)};
  }
`

const VoucherCodeText = styled.span`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(32)};
  line-height: ${rem(34)};
  font-weight: ${props => props.theme.font.primary.weight.superBold};
  color: ${props => props.theme.palette.neutral.default.one};
`

const RedeemingCard = styled.div`
  display: grid;
  place-items: center;
  height: ${rem(136)};
  border-radius: ${rem(8)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const RedeemingLoadingIndicator = styled(LoadingIndicator)`
  padding: 0;
`

const ToggleRow = styled.label`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${rem(12)};
  align-items: center;
  min-height: ${rem(52)};
  padding: 0 ${rem(16)};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${rem(8)};
`

const RedeemSwitch = styled(Switch)`
  --checked-background: ${props => props.theme.palette.neutral.default.one};
  --checked-hover-background: ${props => props.theme.palette.neutral.default.one};
  --checked-stroke: ${props => props.theme.palette.neutral.default.one};

  &:has(:checked:disabled) {
    background-color: ${props => props.theme.palette.neutral.default.one};
    color: ${props => props.theme.palette.neutral.default.one};
    box-shadow: inset 0 0 0 ${rem(1)} ${props => props.theme.palette.neutral.default.one};
  }
`

const StatusCard = styled.div`
  display: grid;
  gap: ${rem(12)};
  padding: ${rem(16)};
  border-radius: ${rem(8)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const SuccessStatusIcon = styled(SolidCheckCircleIcon)`
  color: ${props => props.theme.palette.highlight.primary.darkForeground};
`

const CriticalStatusIcon = styled(SolidExclamationTriangleIcon)`
  color: ${props => props.theme.palette.messaging.critical.normalForeground};
`

const FinalActionRow = styled.div`
  ${mediaQueryUp.tablet} {
    display: flex;
    justify-content: flex-end;
  }
`

const LocationDetail = styled.div`
  display: grid;
  grid-template-columns: ${rem(20)} minmax(0, 1fr);
  column-gap: ${rem(8)};
  align-items: start;

  svg {
    color: ${props => props.theme.palette.neutral.default.one};
  }
`

const RedemptionLinks = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(4)};
  margin-inline-start: ${rem(28)};
`

const EMPTY_THINGS_TO_KNOW: Array<App.ExperienceMarkdownSplitted> = []

interface Props {
  inclusion: ExperiencesPassportInclusion
}

function ExperiencesPassportVoucherModal({ inclusion }: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const modalContext = useModalElementContext<ExperiencesPassportVoucherModalResult>()
  const showModal = useContext(ModalContext)
  const initialVoucherStatus = inclusion.voucherStatus ?? (inclusion.redemptionStatus === 'used' ? 'used' : 'active')
  const markedAsRedeemed = useAppSelector(state =>
    inclusion.experienceItemId ? state.experience.bookingDetails[inclusion.experienceItemId]?.markedAsRedeemed : false,
  )
  const isMarkingAsRedeemed = useAppSelector(state =>
    inclusion.experienceItemId ? state.experience.markingBookingDetailsAsRedeemed[inclusion.experienceItemId] : false,
  )
  const images = getInclusionImages(inclusion)
  const { items: thingsToKnowItems, isLoading: thingsToKnowLoading, isUsingInlineItems } = useThingsToKnow(inclusion.id, EMPTY_THINGS_TO_KNOW)
  const howToRedeem = thingsToKnowItems.find(item => item.key === 'howToRedeem')?.content ??
    (isUsingInlineItems ? inclusion.howToRedeem : undefined)
  const offerConditions = thingsToKnowItems.find(item => item.key === 'offerConditions')?.content ??
    (isUsingInlineItems ? inclusion.offerConditions : undefined)
  const isVoucherRedeemed = markedAsRedeemed || initialVoucherStatus === 'used'
  const isVoucherExpired = initialVoucherStatus === 'expired'
  const isVoucherRedeeming = !isVoucherRedeemed && !isVoucherExpired && isMarkingAsRedeemed
  const isFinalState = isVoucherRedeemed || isVoucherExpired
  const [voucherUsedAt, setVoucherUsedAt] = useState<Date | undefined>(() =>
    inclusion.voucherUsedAt ? new Date(inclusion.voucherUsedAt) : undefined,
  )
  const voucherExpiredAt = useMemo(() =>
    inclusion.voucherExpiredAt ? new Date(inclusion.voucherExpiredAt) : undefined,
  [inclusion.voucherExpiredAt])
  const statusDate = isVoucherExpired ? voucherExpiredAt : voucherUsedAt

  useEffect(() => {
    if (markedAsRedeemed && initialVoucherStatus !== 'used') {
      setVoucherUsedAt(currentVoucherUsedAt => currentVoucherUsedAt ?? new Date())
    }
  }, [initialVoucherStatus, markedAsRedeemed])

  const formattedStatusDate = useMemo(() => {
    if (!statusDate) return undefined

    const weekday = dateFormatter(statusDate, INTL_LONG_WEEKDAY)
    const date = dateFormatter(statusDate, INTL_DMY_CASUAL_SHORT_FORMAT)
    const time = dateFormatter(statusDate, INTL_SHORT_TIME_FORMAT_AM_PM)

    return intl.formatMessage({
      id: 'dbeOP51WkTaIDbh4RIVs',
      defaultMessage: '{weekday}, {date} at {time}',
      description: 'Formatted date and time shown when an Experiences Passport voucher is redeemed or expired',
    }, { weekday, date, time })
  }, [dateFormatter, intl, statusDate])
  const voucherCode = inclusion.voucherCode

  const closeVoucherModal = useCallback<MouseEventHandler<HTMLElement>>((event) => {
    event.stopPropagation()
    modalContext?.resolve(undefined)
  }, [modalContext])

  const closeVoucherModalFromButton = useCallback(() => {
    modalContext?.resolve('backToMyEscapes')
  }, [modalContext])

  const openInformationModal = useCallback((title: string, content: string) => {
    showModal(<Modal title={title}>
      <MarkdownRender content={content} type="compact" />
    </Modal>)
  }, [showModal])

  const openHowToRedeemModal = useCallback(() => {
    if (!howToRedeem) return

    openInformationModal(intl.formatMessage({
      id: 'yx--eHRptJPotv-OGMp-',
      defaultMessage: 'How to redeem?',
      description: 'Title for the Experiences Passport how to redeem modal',
    }), howToRedeem)
  }, [howToRedeem, intl, openInformationModal])

  const openTermsAndConditionsModal = useCallback(() => {
    if (!offerConditions) return

    openInformationModal(intl.formatMessage({
      id: 'vD_hm_3DXTtRXoaYI0UJ',
      defaultMessage: 'T&Cs apply',
      description: 'Title for the Experiences Passport terms and conditions modal',
    }), offerConditions)
  }, [offerConditions, intl, openInformationModal])

  const onRedeemedChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (!event.currentTarget.checked) return
    if (inclusion.experienceItemId) {
      dispatch(markExperienceOrderItemAsRedeemed(inclusion.experienceItemId))
    }
  }, [dispatch, inclusion.experienceItemId])

  return <VoucherModal
    data-testid="experiences-passport-voucher-modal"
    title=""
    dismissible
    onClose={closeVoucherModal}
    flush
    flushVerticalTop
    isHeaderOverlay={!!images.length}
    size="S"
  >
    <Hero className={isFinalState ? 'is-muted' : undefined}>
      {!!images.length && <ImageGallery
        images={images}
        imageParams={EXPERIENCES_PASSPORT_HERO_IMAGE_PARAMS}
        arrowBehaviour="interaction-visible"
        showImageTitles={false}
      />}
      <CarouselControl
        aria-label={intl.formatMessage({
          id: '_WmmEKsrsGXPj9VBwbIl',
          defaultMessage: 'Back',
          description: 'Button label to remove applied content tag filter and go back on search page',
        })}
        className="back"
        kind="secondary"
        variant="default"
        shape="circle"
        size="medium"
        onClick={closeVoucherModal}
      >
        <LineAngleLeftIcon />
      </CarouselControl>
      <CarouselControl
        aria-label={intl.formatMessage({
          id: 'WYXh_8yLMKajGJmG4XY0',
          defaultMessage: 'close',
          description: 'Aria label for close button in modal header dialog window',
        })}
        className="close"
        kind="secondary"
        variant="default"
        shape="circle"
        size="medium"
        onClick={closeVoucherModal}
      >
        <LineTimesIcon />
      </CarouselControl>
    </Hero>
    <Content className={isFinalState ? 'is-final-state' : undefined}>
      <VerticalSpacer gap={24}>
        <VerticalSpacer gap={4}>
          <Heading variant="heading3">{inclusion.title}</Heading>
          {!!inclusion.rating && <Group direction="horizontal" gap={4} verticalAlign="center">
            <StarRatingStar kind="filled" size="XS" />
            <BodyText variant="small">
              <FormattedMessage
                id="bfByel_k25Q8ntz_NCuw"
                defaultMessage="{rating} ({reviewsText})"
                description="Rating and review count for an Experiences Passport inclusion in the list modal"
                values={{
                  rating: inclusion.rating,
                  reviewsText: inclusion.reviewsText,
                }}
              />
            </BodyText>
          </Group>}
          {!!inclusion.description && <BodyText variant="medium" colour="light-foreground-alt">{inclusion.description}</BodyText>}
          {!!inclusion.value && <BodyText variant="medium" weight="bold" colour="highlight-primary-dark">
            <FormattedMessage
              id="pojZNR-VAugwT6mDuIHl"
              defaultMessage="Valued up to {value}"
              description="Experiences Passport inclusion monetary value shown in the item detail modal"
              values={{
                value: <FormatCurrency
                  value={inclusion.value}
                  currency={inclusion.valueCurrencyCode}
                  format="roundedDollar"
                />,
              }}
            />
          </BodyText>}
        </VerticalSpacer>

        {!isFinalState && !isVoucherRedeeming && voucherCode && <VerticalSpacer gap={20}>
          <VoucherCard>
            <Group direction="vertical" gap={4}>
              <Group direction="vertical" gap={4}>
                <Caption variant="small" weight="bold">
                  <FormattedMessage
                    id="yheQ9_1cIGoabfjJRSkt"
                    defaultMessage="VOUCHER CODE"
                    description="Label shown above the visible code for an Experiences Passport voucher"
                  />
                </Caption>
                <VoucherCodeRow>
                  <VoucherCodeText>{voucherCode}</VoucherCodeText>
                  <CopyToClipboardClickableIcon value={voucherCode} stopPropagationOnCopy />
                </VoucherCodeRow>
              </Group>
              <BodyText variant="small" colour="normal-contrast">
                <FormattedMessage
                  id="Jg5MdId3Xk-3dmXcjaJW"
                  defaultMessage="Present this voucher to venue staff."
                  description="Instruction for using an Experiences Passport voucher"
                />
              </BodyText>
            </Group>
            <QrCodeCard>
              <ExperiencesQRCode value={voucherCode} size={88} />
            </QrCodeCard>
          </VoucherCard>
          {!thingsToKnowLoading && !!howToRedeem && <TextLink size="medium" onClick={openHowToRedeemModal}>
            <FormattedMessage
              id="l7n9JSUfh9Q-2qwrPieL"
              defaultMessage="How to redeem?"
              description="Link to open the Experiences Passport how to redeem modal"
            />
          </TextLink>}
          <ToggleRow>
            <BodyText variant="medium" weight="bold">
              <FormattedMessage
                id="QtuwoZeOC3E6julvMQ3W"
                defaultMessage="Mark as redeemed"
                description="Toggle label to mark an Experiences Passport voucher as redeemed"
              />
            </BodyText>
            <RedeemSwitch checked={false} onChange={onRedeemedChange} />
          </ToggleRow>
        </VerticalSpacer>}

        {isVoucherRedeeming && <VerticalSpacer gap={16}>
          <RedeemingCard>
            <RedeemingLoadingIndicator inline ballSize={10} />
          </RedeemingCard>
          <ToggleRow>
            <BodyText variant="medium" weight="bold">
              <FormattedMessage
                id="QtuwoZeOC3E6julvMQ3W"
                defaultMessage="Mark as redeemed"
                description="Toggle label to mark an Experiences Passport voucher as redeemed"
              />
            </BodyText>
            <RedeemSwitch checked disabled />
          </ToggleRow>
        </VerticalSpacer>}

        {isVoucherRedeemed && <StatusCard>
          <Group direction="horizontal" gap={4} verticalAlign="center">
            <SuccessStatusIcon size="S" />
            <BodyText variant="large" weight="bold">
              <FormattedMessage
                id="eTSeiPTcGA3T51JHUOrM"
                defaultMessage="Voucher redeemed"
                description="Confirmation title after an Experiences Passport voucher has been redeemed"
              />
            </BodyText>
          </Group>
          <BodyText variant="small" colour="normal-contrast">
            {inclusion.voucherUsedAtText ?? (formattedStatusDate ? <FormattedMessage
              id="vCUTpNTeLeloxa40GJgi"
              defaultMessage="Your voucher has been marked as used on {dateText}"
              description="Confirmation text with date after an Experiences Passport voucher has been redeemed"
              values={{
                dateText: <strong>{formattedStatusDate}</strong>,
              }}
            /> : <FormattedMessage
              id="TgUBR-1w7afiqbQMsaDg"
              defaultMessage="Your voucher has been marked as used."
              description="Confirmation text shown when a redeemed Experiences Passport voucher has no recorded redemption date"
            />)}
          </BodyText>
        </StatusCard>}

        {isVoucherExpired && <StatusCard>
          <Group direction="horizontal" gap={4} verticalAlign="center">
            <CriticalStatusIcon size="S" />
            <BodyText variant="large" weight="bold">
              <FormattedMessage
                id="nXh2JK7oVL1QbkKVoBfE"
                defaultMessage="Voucher has expired"
                description="Status title for an expired Experiences Passport voucher"
              />
            </BodyText>
          </Group>
          <BodyText variant="small" colour="normal-contrast">
            {inclusion.voucherExpiredAtText ?? (formattedStatusDate ? <FormattedMessage
              id="bKhZjYdA_bleumnTbsFT"
              defaultMessage="Your voucher expired on {dateText}"
              description="Status text with date for an expired Experiences Passport voucher"
              values={{
                dateText: <strong>{formattedStatusDate}</strong>,
              }}
            /> : <FormattedMessage
              id="cOPhF_diG0ZJG1omiCJe"
              defaultMessage="Your voucher has expired."
              description="Status text shown when an expired Experiences Passport voucher has no recorded expiration date"
            />)}
          </BodyText>
        </StatusCard>}

        {!isFinalState && <VerticalSpacer gap={12}>
          {!!inclusion.locationText && <LocationDetail>
            <LineMapMarkerIcon size="S" />
            <Group direction="vertical" gap={2}>
              <BodyText variant="medium" weight="bold">{inclusion.locationText}</BodyText>
              {!!inclusion.locationAddressText && <BodyText variant="medium">
                {inclusion.locationAddressText}
              </BodyText>}
              {!!inclusion.locationDistanceText && <BodyText variant="medium" weight="bold" colour="highlight-primary-dark">
                {inclusion.locationDistanceText}
              </BodyText>}
            </Group>
          </LocationDetail>}
          <BodyTextBlock variant="medium" startIcon={<LineInfoCircleIcon />}>
            <FormattedMessage
              id="-fSVVgeKhMagqnFxOeqA"
              defaultMessage="Redeem during your stay."
              description="Default redemption instructions for an Experiences Passport inclusion"
            />
          </BodyTextBlock>
          {!thingsToKnowLoading && !!offerConditions && <RedemptionLinks>
            <TextLink size="medium" onClick={openTermsAndConditionsModal}>
              <FormattedMessage
                id="bKARDo8ar_ZyS59OMTFP"
                defaultMessage="T&Cs apply"
                description="Link to open the Experiences Passport terms and conditions modal"
              />
            </TextLink>
          </RedemptionLinks>}
        </VerticalSpacer>}

        {!isFinalState && <ExperiencesPassportRouteMap inclusion={inclusion} />}

        {isFinalState && <FinalActionRow>
          <TextButton kind="primary" fit="mobile-full-width" onClick={closeVoucherModalFromButton}>
            <FormattedMessage
              id="DvaU-rOIBN3Z4AyaseY7"
              defaultMessage="Back to My Escapes"
              description="Button label to return from an Experiences Passport voucher status modal to My Escapes"
            />
          </TextButton>
        </FinalActionRow>}
      </VerticalSpacer>
    </Content>
  </VoucherModal>
}

export default ExperiencesPassportVoucherModal
